import React, { useState, useEffect } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Menu,
  MenuItem,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import TableTopControls from "common/TableTopControls";

import SedesApi from "api/sedesApi";
import EditSedeModal from "./editSedeModal";

const SedesTable = () => {
  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSede, setSelectedSede] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleEdit = (sede) => {
    setSelectedSede(sede);
    setEditModalOpen(true);
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro(a) de eliminar esta sede?")) {
      try {
        await SedesApi.deleteSede(id);
        fetchSedes();
      } catch (error) {
        console.error("Error al eliminar la sede:", error);
      }
    }
    handleMenuClose();
  };

  const fetchSedes = async () => {
    try {
      const response = await SedesApi.fetchSedes();
      setSedes(response.data);
    } catch (error) {
      console.error("Error al obtener sedes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSedes();
  }, []);

  const filteredSedes = sedes.filter((sede) => {
    const matchesSearch =
      sede.ciudad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sede.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sede.direccion?.toLowerCase().includes(searchTerm.toLowerCase());
    // sede.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    // sede.direccion.toLowerCase().includes(searchTerm.toLowerCase()) ||
    // sede.director.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const paginatedSedes = filteredSedes.slice(page * pageSize, (page + 1) * pageSize);

  const columns = [
    {
      field: "actions",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "ciudad",
      headerName: "Ciudad",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.ciudad}
        </SuiTypography>
      ),
    },
    {
      field: "nombre",
      headerName: "Nombre",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.nombre}
        </SuiTypography>
      ),
    },
    {
      field: "director",
      headerName: "Director",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.responsable_detalle?.director.nombre_completo || ""}
        </SuiTypography>
      ),
    },
    {
      field: "sub_director",
      headerName: "Subdirector",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.sub_director || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "coordinador",
      headerName: "Coordinador",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.responsable_detalle?.coordinador.nombre_completo || ""}
        </SuiTypography>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.email || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "celular",
      headerName: "Celular",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.celular || "No tiene."}
        </SuiTypography>
      ),
    },
  ];

  return (
    <SuiBox>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <SuiBox
          justifyContent="space-between"
          alignItems="center"
          p={1}
          sx={{
            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          }}
        >
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiBox display="flex" flexDirection="column">
                <strong>Administración de Sedes</strong>
              </SuiBox>
            </Grid>
          </Grid>
          <SuiBox display="flex" alignItems="center" gap={2}>
            <Tooltip title="Buscar por Ciudad, Nombre y Dirección" arrow>
              <TextField
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Tooltip>
            <TableTopControls
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalRows={filteredSedes.length}
            />
            <SuiButton
              variant="gradient"
              buttonColor="dark"
              size="small"
              onClick={() => {
                setSelectedSede(null);
                setEditModalOpen(true);
              }}
            >
              Nueva Sede
            </SuiButton>
          </SuiBox>
        </SuiBox>

        <DataGrid
          rows={paginatedSedes}
          columns={columns}
          disableSelectionOnClick
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[100],
              },
              minHeight: "48px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "48px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleEdit(sedes.find((a) => a.id === selectedId))}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedId)} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      <EditSedeModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        sede={selectedSede}
        onSuccess={() => {
          fetchSedes();
          setEditModalOpen(false);
        }}
      />
    </SuiBox>
  );
};

export default SedesTable;
